import { Injectable } from '@angular/core';

import {
  BehaviorSubject,
  combineLatest,
  fromEvent,
  merge,
  Observable,
} from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';

import { INotificationBufferItem } from '@ispui/notification';

import { filterDefined } from 'utils/is-defined';

import { NotificationErrorOptions } from './model/notification-error-options';
import { ISPNotificationEvent } from './model/notification-event.type';
import { ISPNotificationOpenOptions } from './model/notification-open-options.interface';
import { ISPNotification } from './model/notification.interface';

/**
 * Notifications service. Used for displaying notifications popups
 */
@Injectable()
export class NotificationService {
  private readonly bottomGroupSubject: BehaviorSubject<HTMLIspuiNotificationGroupElement | null> =
    new BehaviorSubject(null);

  private readonly bottomGroup$ = this.bottomGroupSubject.pipe(filterDefined());

  set bottomGroup(group: HTMLIspuiNotificationGroupElement) {
    this.bottomGroupSubject.next(group);
  }

  get bottomGroup(): HTMLIspuiNotificationGroupElement {
    if (!this.bottomGroupSubject.value) {
      throw new Error('bottomGroupSubject has null!');
    }
    return this.bottomGroupSubject.value;
  }

  private readonly topGroupSubject: BehaviorSubject<HTMLIspuiNotificationGroupElement | null> =
    new BehaviorSubject(null);

  private readonly topGroup$ = this.topGroupSubject.pipe(filterDefined());

  set topGroup(group: HTMLIspuiNotificationGroupElement) {
    this.topGroupSubject.next(group);
  }

  get topGroup(): HTMLIspuiNotificationGroupElement {
    if (!this.topGroupSubject.value) {
      throw new Error('topGroupSubject has null!');
    }
    return this.topGroupSubject.value;
  }

  /**
   * All notifications events in group stream
   */
  readonly events$ = combineLatest([this.bottomGroup$, this.topGroup$]).pipe(
    switchMap(([bottomGroup, topGroup]) =>
      merge(
        fromEvent(bottomGroup, 'notification-group-event'),
        fromEvent(topGroup, 'notification-group-event'),
      ),
    ),
  ) as Observable<ISPNotificationEvent>;

  /**
   * Get notification event stream by notification id
   *
   * @param id - notification id
   */
  getEventsById$(id: number | string): Observable<ISPNotificationEvent> {
    return new Observable(observer => {
      const sub = this.events$
        .pipe(filter(event => event.detail.id === String(id)))
        .subscribe(event => {
          observer.next(event);

          if (event.detail.animationState === 'hide-end') {
            observer.complete();
          }
        });

      return () => sub.unsubscribe();
    });
  }

  /**
   * Show error notification
   *
   * @param errorOptions - Params for error noitifcation
   * @param errorOptions.title - Error title
   * @param errorOptions.message - Error message
   * @param errorOptions.link - Link button for handle user action
   * @param errorOptions.notificationId - Id for notify
   * @param errorOptions.addingOptions - Adding options for notification
   */
  addError$({
    title,
    message,
    link,
    notificationId,
    addingOptions: options,
  }: NotificationErrorOptions): Observable<ISPNotificationEvent> {
    const id = notificationId ? notificationId : Date.now();
    const notification: ISPNotification = {
      notificationId: id,
      title,
      message,
      status: 'danger',
      buttons: link
        ? [
            {
              text: link.text,
              type: 'link',
              callback: link.callback,
            },
          ]
        : undefined,
      data: {
        source: 'internal',
      },
    };
    return this.add$(notification, options);
  }

  /**
   * Add notification to group
   *
   * @param notification - notification meta
   * @param options
   */
  add$(
    notification: ISPNotification,
    options?: ISPNotificationOpenOptions,
  ): Observable<ISPNotificationEvent> {
    if (!notification.notificationId) {
      notification.notificationId = Date.now();
    }

    if (options?.place === 'top') {
      this.topGroup.add(notification, options);
    } else {
      this.bottomGroup.add(notification, options);
    }

    return this.getEventsById$(notification.notificationId);
  }

  /**
   * Visually hide all notifications
   */
  closeAll(): void {
    this.bottomGroup.closeAll();
    this.topGroup.closeAll();
  }

  /**
   * Close notification by it's id
   *
   * @param id - notification id or array of ids
   */
  closeById(id: (number | string)[] | number | string): void {
    this.bottomGroup.closeById(id);
    this.topGroup.closeById(id);
  }

  /**
   * Check that notification id or ids is currently existed
   *
   * @param id - id or ids of notifications
   */
  async isSomeExistById(
    id: (number | string)[] | number | string,
  ): Promise<boolean> {
    const ids = Array.isArray(id) ? id : [id];
    const fixedIds = ids.map(i => String(i));

    const currentNotifications = await this.getAll();
    const currentNotificationsIds = currentNotifications.map(n =>
      String(n.meta.notificationId),
    );

    return currentNotificationsIds.some(i => fixedIds.includes(i));
  }

  /** Get all notifications in group */
  getAll(): Promise<INotificationBufferItem[]> {
    return Promise.all([
      this.bottomGroup.getAll(),
      this.topGroup.getAll(),
    ]).then(([bottom, top]) => [...bottom, ...top]);
  }
}
